import { useState } from 'react'
import { GlassCard, ScrollReveal, MagneticButton } from '@/components/ui'
import { Mail, MessageSquare, Clock, Send, Loader2 } from 'lucide-react'
import { useContactForm, Toast } from '@lukhas/ui'

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    honeypot: ''
  })

  const [showToast, setShowToast] = useState(false)

  const { submitForm, isSubmitting, error } = useContactForm({
    endpoint: '/api/contact',
    onSuccess: () => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        honeypot: ''
      })
    }
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitForm(formData)
    setShowToast(true)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }))
  }

  return (
    <>
      <ScrollReveal direction="up" blur>
        <section className="pb-16 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <div className="mb-6 flex justify-center">
              <MessageSquare className="w-16 h-16 text-domain-id-purple animate-pulse" strokeWidth={1.5} />
            </div>
            <h1 className="text-5xl md:text-6xl font-light tracking-[0.15em] mb-6 text-text-primary">
              Get In Touch
            </h1>
            <p className="text-xl text-text-primary/80 max-w-3xl mx-auto">
              Have questions about LUKHAS consciousness technology? We're here to help.
            </p>
          </div>
        </section>
      </ScrollReveal>

      <ScrollReveal direction="up" blur>
        <section className="py-8 px-6">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <GlassCard>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <Mail className="w-8 h-8 text-domain-id-purple" />
                    <div>
                      <h3 className="text-xl font-light text-text-primary">General Inquiries</h3>
                      <a href="mailto:hello@lukhas.id" className="text-domain-id-purple hover:underline">
                        hello@lukhas.id
                      </a>
                    </div>
                  </div>
                </div>
              </GlassCard>

              <GlassCard>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <Send className="w-8 h-8 text-luke-gold" />
                    <div>
                      <h3 className="text-xl font-light text-text-primary">Technical Support</h3>
                      <a href="mailto:support@lukhas.id" className="text-domain-id-purple hover:underline">
                        support@lukhas.id
                      </a>
                    </div>
                  </div>
                </div>
              </GlassCard>

              <GlassCard>
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <Clock className="w-8 h-8 text-lukhas-success" />
                    <div>
                      <h3 className="text-xl font-light text-text-primary">Response Time</h3>
                      <p className="text-text-primary/70">Usually within 24-48 hours</p>
                    </div>
                  </div>
                </div>
              </GlassCard>
            </div>

            <GlassCard>
              <div className="p-8">
                <h2 className="text-2xl font-light tracking-wide mb-6 text-text-primary">Send Us A Message</h2>
                {/* Implemented: Wire form to unified contact intake endpoint with anti-spam, debounce, and notifications */}
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleChange}
                    style={{ display: 'none' }}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                  <div>
                    <label className="block text-sm text-text-primary/70 mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-space border border-domain-id-purple/30 rounded-lg text-text-primary focus:outline-none focus:border-domain-id-purple"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-primary/70 mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-space border border-domain-id-purple/30 rounded-lg text-text-primary focus:outline-none focus:border-domain-id-purple"
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-primary/70 mb-2">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-space border border-domain-id-purple/30 rounded-lg text-text-primary focus:outline-none focus:border-domain-id-purple"
                      placeholder="What's this about?"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-text-primary/70 mb-2">Message</label>
                    <textarea
                      rows={6}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-deep-space border border-domain-id-purple/30 rounded-lg text-text-primary focus:outline-none focus:border-domain-id-purple resize-none"
                      placeholder="Tell us how we can help..."
                    />
                  </div>
                  <MagneticButton
                    domain="id"
                    variant="primary"
                    size="lg"
                    type="submit"
                    className="w-full disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Send Message'}
                  </MagneticButton>
                </form>
              </div>
            </GlassCard>
          </div>
        </section>
      </ScrollReveal>
      {showToast && (
        <Toast
          message={error || 'Message sent successfully!'}
          type={error ? 'error' : 'success'}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  )
}
